import { useCallback, useState } from "react";
import { Button, Modal, Spinner, TextArea, toast } from "@heroui/react";
import { Pencil } from "lucide-react";
import type { MyBlacklistUser } from "@repo/api/blacklist.type";
import { UserAvatar } from "../../../components/User";
import { EvidenceImagePicker } from "./EvidenceImagePicker";
import type { EvidenceSlot } from "./types";
import { collectEvidenceKeys, evidenceToDisplayUrl } from "./utils";
import { useUpdateBlacklistEntry } from "./use-blacklist";

const MAX_IMAGES = 9;

export type EditBlacklistEntryFormProps = {
  item: MyBlacklistUser;
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  cdnBase: string;
  onSuccess: () => void;
  onCancel?: () => void;
  isDisabled?: boolean;
};

export function EditBlacklistEntryForm({
  item,
  uploadFile,
  cdnBase,
  onSuccess,
  onCancel,
  isDisabled = false,
}: EditBlacklistEntryFormProps) {
  const [reason, setReason] = useState(item.reason ?? "");
  const [slots, setSlots] = useState<EvidenceSlot[]>(() =>
    (item.evidence ?? []).map((key, i) => ({
      kind: "existing" as const,
      id: `ex-${item.uid}-${i}-${key}`,
      fileKey: key,
      url: evidenceToDisplayUrl(key, cdnBase),
    })),
  );
  // 成功 / 失败 toast 与黑名单缓存失效都在 mutation 里统一处理
  const updateEntry = useUpdateBlacklistEntry();
  const loading = updateEntry.isPending;

  const handleSubmit = () => {
    if (slots.some((s) => s.kind === "pending" && s.status === "uploading")) {
      toast.danger("请等待图片上传完成");
      return;
    }
    if (slots.some((s) => s.kind === "pending" && s.status === "error")) {
      toast.danger("请先删除上传失败的图片");
      return;
    }
    const keys = collectEvidenceKeys(slots);

    updateEntry.mutate(
      { uid: item.uid, reason: reason.trim() || undefined, images: keys },
      { onSuccess },
    );
  };

  const uploading = slots.some((s) => s.kind === "pending" && s.status === "uploading");

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-default-200 p-3 bg-default-50">
        <div className="flex items-center gap-2">
          <UserAvatar avatar_url={item.avatar_url} size="sm" />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-900">{item.nickname}</span>
            <span className="text-xs text-gray-400">{item.uid}</span>
          </div>
        </div>
      </div>
      <TextArea
        placeholder="输入理由（可选）"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        disabled={isDisabled || loading}
      />
      <div className="flex flex-col gap-2">
        <span className="text-sm text-gray-600">
          举证图片（最多 {MAX_IMAGES} 张，可删除旧图或上传新图，单张不超过 10MB）
        </span>
        <span className="text-red-500 text-sm">
          请提供详细的证据链条，举证内容包括但不限于（交易记录、聊天记录、流模证据等），您需要自行为此信息的真实性负责。严禁篡改、扭曲、伪造事实，如对方提出申诉且无法证明其真实性时，您将永久失去该系统的访问权限。
        </span>
        <EvidenceImagePicker
          slots={slots}
          onSlotsChange={setSlots}
          uploadFile={uploadFile}
          maxCount={MAX_IMAGES}
          disabled={isDisabled || loading}
        />
      </div>
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button variant="tertiary" onPress={onCancel} isDisabled={loading}>
            取消
          </Button>
        ) : null}
        <Button
          variant="primary"
          onPress={handleSubmit}
          isPending={loading}
          isDisabled={isDisabled || uploading}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              保存
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export type EditEvidenceButtonProps = {
  item: MyBlacklistUser;
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  cdnBase: string;
  isDisabled?: boolean;
  onSaved?: () => void;
};

/** 打开编辑弹窗的按钮；表单本体见 EditBlacklistEntryForm */
export function EditEvidenceButton({
  item,
  uploadFile,
  cdnBase,
  isDisabled = false,
  onSaved,
}: EditEvidenceButtonProps) {
  const [open, setOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setFormKey((k) => k + 1);
    setOpen(true);
  }, []);

  return (
    <Modal>
      <Button
        size="sm"
        variant="tertiary"
        className="rounded-full"
        isDisabled={isDisabled}
        onPress={handleOpen}
      >
        <Pencil size={14} />
        编辑
      </Button>
      <Modal.Backdrop
        isOpen={open}
        onOpenChange={(next: boolean) => {
          if (!next) handleClose();
        }}
      >
        <Modal.Container size="lg" scroll="inside">
          <Modal.Dialog>
            {({ close }: { close: () => void }) => (
              <>
                <Modal.Header>
                  <Modal.Heading>编辑黑名单记录</Modal.Heading>
                </Modal.Header>
                <Modal.Body>
                  <EditBlacklistEntryForm
                    key={formKey}
                    item={item}
                    uploadFile={uploadFile}
                    cdnBase={cdnBase}
                    isDisabled={isDisabled}
                    onCancel={() => {
                      close();
                      handleClose();
                    }}
                    onSuccess={() => {
                      onSaved?.();
                      close();
                      handleClose();
                    }}
                  />
                </Modal.Body>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
